/**
 * AES-CBC encryption utility that matches the backend DecryptValueAsync implementation.
 * Uses PBKDF2 key derivation with SHA-256, 100,000 iterations.
 * The IV is prepended to the ciphertext before base64 encoding.
 */

export interface EncryptionKey {
  key: string;
  passphrase: string;
}

/**
 * Convert Uint8Array to base64 string (standard base64, not URL-safe)
 */
const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Encrypts a value using AES-CBC with PBKDF2 key derivation.
 * Compatible with the backend's DecryptValueAsync method.
 *
 * Backend uses: Rfc2898DeriveBytes(secretKey.Key, Encoding.UTF8.GetBytes(secretKey.Passphrase), 100_000, SHA256)
 * - secretKey.Key = password (first param)
 * - secretKey.Passphrase = salt (second param)
 *
 * @param plainText - The value to encrypt
 * @param secretKey - The encryption key containing key and passphrase
 * @returns Base64 encoded string with IV prepended to ciphertext
 */
export const encryptValue = async (
  plainText: string,
  secretKey: EncryptionKey,
): Promise<string> => {
  if (!plainText) {
    return plainText;
  }

  // Validate that we have encryption keys
  if (!secretKey.key || !secretKey.passphrase) {
    console.warn('Encryption keys not configured, returning plain text');
    return plainText;
  }

  const encoder = new TextEncoder();

  // In .NET Rfc2898DeriveBytes:
  // - First param (secretKey.Key) is the PASSWORD
  // - Second param (secretKey.Passphrase) is the SALT
  const password = encoder.encode(secretKey.key);
  const salt = encoder.encode(secretKey.passphrase);

  // Import the password as key material for PBKDF2
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    password,
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  // Derive 32 bytes (256 bits) for AES-256 key using PBKDF2
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256, // 32 bytes * 8 bits
  );

  // Import the derived key for AES-CBC encryption
  const aesKey = await crypto.subtle.importKey(
    'raw',
    derivedBits,
    { name: 'AES-CBC' },
    false,
    ['encrypt'],
  );

  // Generate a random 16-byte IV
  const iv = crypto.getRandomValues(new Uint8Array(16));

  // Encrypt the plaintext (Web Crypto API uses PKCS7 padding by default for AES-CBC)
  const plainTextBytes = encoder.encode(plainText);
  const cipherText = await crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    aesKey,
    plainTextBytes,
  );

  // Combine IV + ciphertext (IV is prepended, matching backend expectation)
  const cipherBytes = new Uint8Array(iv.length + cipherText.byteLength);
  cipherBytes.set(iv, 0);
  cipherBytes.set(new Uint8Array(cipherText), iv.length);

  // Convert to standard base64 (matching .NET Convert.ToBase64String)
  return uint8ArrayToBase64(cipherBytes);
};

/**
 * Encrypts the idNo field in an applicant object
 */
export const encryptApplicantIdNo = async <
  T extends { idNo: string; idType?: string },
>(
  applicant: T,
  secretKey: EncryptionKey,
): Promise<T> => {
  if (!applicant.idNo) {
    return applicant;
  }

  const encryptedIdNo = await encryptValue(applicant.idNo, secretKey);
  return {
    ...applicant,
    idNo: encryptedIdNo,
  };
};

/**
 * Encrypts idNo for all applicants in an array
 */
export const encryptApplicantsIdNo = async <
  T extends { idNo: string; idType?: string },
>(
  applicants: T[],
  secretKey: EncryptionKey,
): Promise<T[]> => {
  return Promise.all(
    applicants.map((applicant) => encryptApplicantIdNo(applicant, secretKey)),
  );
};
